/**
 * Core sunscreen recommendation logic and UV utilities.
 *
 * Contains the needsSunscreen() algorithm that integrates UV dose over time and
 * compares it against Minimal Erythemal Dose (MED) thresholds per Fitzpatrick
 * skin type. Adjustments are applied for cloud cover, surface reflection, and
 * altitude. Also exports helper functions for safe exposure time, reapplication
 * timing, background/text colors based on UV level, and duration formatting.
 */

// Minimal Erythemal Dose (MED) by Fitzpatrick skin type
// The UV dose threshold at which skin damage begins
export const MINIMAL_ERYTHEMAL_DOSE = {
  1: 2,    // Type I - Always burns, never tans
  2: 2.5,  // Type II - Burns easily, tans minimally
  3: 3.5,  // Type III - Sometimes burns, slowly tans
  4: 4.5,  // Type IV - Burns minimally, always tans
  5: 7,    // Type V - Rarely burns, tans well
  6: 11.5, // Type VI - Never burns, deeply pigmented
};

// Cloud cover factors
export const CLOUD_COVER_FACTORS = {
  clear: 1,       // Clear sky
  scattered: 0.9, // Scattered clouds
  broken: 0.73,   // Broken clouds
  overcast: 0.32  // Overcast
};

// Surface reflection factors
export const SURFACE_FACTORS = {
  snow: 1.85,     // Fresh snow
  sand: 1.35,     // Beach sand
  water: 1.06,    // Water
  grass: 1.25,    // Grass
  concrete: 1.3,  // Concrete
  asphalt: 1.08,  // Asphalt
  soil: 1.17,     // Soil
  none: 1.0       // No special surface
};

// MED reduction percentages for other risk factors
export const OTHER_FACTOR_MED_REDUCTIONS = {
  pregnant: 0.20,
  breastfeeding: 0.10,
  skinCancer: 0.20,
  rosacea: 0.30,
  eczema: 0.25,
  photosensitizing: 0.40,
  acne: 0.05,
  antibiotics: 0.40,
  retinoids: 0.05,
  chemotherapy: 0.30,
  immunosuppressants: 0.35,
  diuretics: 0.25,
  tattoos: 0.50,
  recentProcedures: 0.50,
};

/**
 * Calculate adjusted MED threshold after applying other risk factor reductions.
 * Reductions stack additively but MED won't drop below 10% of the base value.
 * @param {number} baseMED - Base MED for the skin type
 * @param {Object} otherFactors - Object with factor keys mapped to booleans
 * @returns {number} Adjusted MED threshold
 */
const getAdjustedMED = (baseMED, otherFactors) => {
  if (!otherFactors) return baseMED;

  let totalReduction = 0;
  for (const [key, active] of Object.entries(otherFactors)) {
    if (active && OTHER_FACTOR_MED_REDUCTIONS[key]) {
      totalReduction += OTHER_FACTOR_MED_REDUCTIONS[key];
    }
  }

  // Cap so MED doesn't drop below 10% of base
  const factor = Math.max(0.1, 1 - totalReduction);
  return baseMED * factor;
};

// UV absorption factor
export const ABSORPTION_FACTOR = 0.9;

// Skin type data with colors and descriptions
export const SKIN_TYPE_DATA = {
  1: {
    label: "Type I - Very Fair",
    color: "#edd8c6",
    description: "Always burns, never tans",
  },
  2: {
    label: "Type II - Fair",
    color: "#e1b49f",
    description: "Usually burns, tans minimally",
  },
  3: {
    label: "Type III - Medium",
    color: "#daa78c",
    description: "Sometimes burns, tans gradually",
  },
  4: {
    label: "Type IV - Olive",
    color: "#d6987c",
    description: "Rarely burns, always tans",
  },
  5: {
    label: "Type V - Brown",
    color: "#8e5f4b",
    description: "Very rarely burns, tans deeply",
  },
  6: {
    label: "Type VI - Black",
    color: "#482b1a",
    description: "Never burns, deeply pigmented",
  },
};

// UV index background colors
export const UV_COLORS = {
  0: "#B0E0E6",  // Powder Blue - very gentle
  1: "#ADD8E6",  // Light Blue
  2: "#87CEFA",  // Light Sky Blue
  3: "#FFF8DC",  // Cornsilk - very light yellow
  4: "#FFEB3B",  // Yellow
  5: "#FFD700",  // Gold
  6: "#FFA500",  // Orange
  7: "#FF8C00",  // Dark Orange
  8: "#FF6347",  // Tomato
  9: "#FF4500",  // Orange Red
  10: "#DC143C", // Crimson
};

/**
 * Calculate total UV dose over time period
 * @param {Array} hourlyUVData - Array of {uvi: number} for each hour
 * @param {number} duration - Duration in minutes
 * @returns {number} Total UV dose
 */
export const calculateUVDose = (hourlyUVData, duration) => {
  if (!hourlyUVData || hourlyUVData.length === 0) return 0;
  
  const hoursNeeded = Math.ceil(duration / 60);
  let totalUV = 0;
  
  // Sum up UV for each hour of exposure
  for (let i = 0; i < Math.min(hoursNeeded, hourlyUVData.length); i++) {
    const hourlyUV = hourlyUVData[i]?.uvi || hourlyUVData[i] || 0;
    
    // For the last partial hour, scale by the fraction of the hour
    if (i === hoursNeeded - 1 && duration % 60 !== 0) {
      totalUV += hourlyUV * ((duration % 60) / 60);
    } else {
      totalUV += hourlyUV;
    }
  }
  
  return totalUV;
};

/**
 * Calculate if sunscreen is needed using UV dose integration
 * @param {number|Array} uvData - Current UV index or array of hourly UV data
 * @param {number} skinType - Fitzpatrick skin type (1-6)
 * @param {number} duration - Planned time outdoors in minutes
 * @param {string} cloudCover - Cloud cover type (clear, scattered, broken, overcast)
 * @param {string} surface - Surface type (snow, sand, water, grass, concrete, asphalt, soil, none)
 * @param {number} elevation - Elevation in feet
 * @returns {boolean} Whether sunscreen is needed
 */
export const needsSunscreen = (uvData, skinType, duration, cloudCover = 'clear', surface = 'none', elevation = 0, otherFactors = null) => {
  // Calculate base UV dose
  let uvDose;
  if (Array.isArray(uvData)) {
    uvDose = calculateUVDose(uvData, duration);
  } else {
    // Single UV index value - use for entire duration
    uvDose = (uvData * duration) / 60; // Convert to hourly dose
  }
  
  if (uvDose === 0) return false;
  
  // Apply absorption factor
  uvDose *= ABSORPTION_FACTOR;
  
  // Apply cloud cover factor
  const cloudFactor = CLOUD_COVER_FACTORS[cloudCover] || 1;
  uvDose *= cloudFactor;
  
  // Apply surface reflection factor
  const surfaceFactor = SURFACE_FACTORS[surface] || 1;
  uvDose *= surfaceFactor;
  
  // Apply altitude factor (convert feet to kilometers)
  const altitudeKm = elevation * 0.0003048;
  const altitudeFactor = 1 + (altitudeKm * 0.06);
  uvDose *= altitudeFactor;
  
  // Get MED threshold for skin type, adjusted for other risk factors
  const medThreshold = getAdjustedMED(MINIMAL_ERYTHEMAL_DOSE[skinType], otherFactors);
  
  return uvDose > medThreshold;
};

/**
 * Get safe exposure time without sunscreen using new dose calculation
 * @param {number} uvIndex - Current UV index (0-11+)
 * @param {number} skinType - Fitzpatrick skin type (1-6)
 * @param {string} cloudCover - Cloud cover type
 * @param {string} surface - Surface type
 * @param {number} elevation - Elevation in feet
 * @returns {number|null} Safe exposure time in minutes, or null if UV is 0
 */
export const getSafeExposureTime = (uvIndex, skinType, cloudCover = 'clear', surface = 'none', elevation = 0, otherFactors = null) => {
  if (uvIndex === 0) return null;

  const medThreshold = getAdjustedMED(MINIMAL_ERYTHEMAL_DOSE[skinType], otherFactors);
  const cloudFactor = CLOUD_COVER_FACTORS[cloudCover] || 1;
  const surfaceFactor = SURFACE_FACTORS[surface] || 1;
  const altitudeKm = elevation * 0.0003048;
  const altitudeFactor = 1 + (altitudeKm * 0.06);
  
  // Reverse calculate safe time from MED threshold
  // MED = (UV * time/60) * 0.9 * cloudFactor * surfaceFactor * altitudeFactor
  // time = (MED * 60) / (UV * 0.9 * cloudFactor * surfaceFactor * altitudeFactor)
  const safeTime = (medThreshold * 60) / (uvIndex * ABSORPTION_FACTOR * cloudFactor * surfaceFactor * altitudeFactor);
  
  return Math.round(safeTime);
};

/**
 * Calculate sunscreen reapplication time
 * @param {number} uvIndex - Current UV index (0-11+)
 * @param {number} skinType - Fitzpatrick skin type (1-6)
 * @param {number} duration - Planned time outdoors in minutes
 * @returns {number|null} Reapplication time in minutes, or null if no sunscreen needed
 */
export const getReapplicationTime = (uvIndex, skinType, duration) => {
  if (!needsSunscreen(uvIndex, skinType, duration)) return null;

  // SPF 30+ typically needs reapplication every 2 hours
  // Adjust based on UV intensity and activity
  let baseTime = 120; // 2 hours in minutes

  if (uvIndex > 7) baseTime = 90;  // 1.5 hours for very high UV
  if (uvIndex > 10) baseTime = 60; // 1 hour for extreme UV

  return Math.min(baseTime, duration);
};

/**
 * Get background color based on UV index and darkness state
 * @param {number} uvIndex - Current UV index (0-11+)
 * @param {boolean} isDark - Whether it's currently dark (after sunset)
 * @returns {string} Hex color code
 */
export const getBackgroundColor = (uvIndex, isDark = false) => {
  // If it's dark (after sunset), use almost black background
  if (isDark) {
    return "#0A0A0A"; // Very dark gray, almost black
  }
  
  return UV_COLORS[uvIndex] || "#8B0000"; // Dark Red for extreme (11+)
};

/**
 * Get a contrasting background color for the app features section
 * @param {number} uvIndex - Current UV index (0-11+)  
 * @param {boolean} isDark - Whether it's currently dark (after sunset)
 * @returns {string} Hex color code for app features background
 */
export const getAppFeaturesBackgroundColor = (uvIndex, isDark = false) => {
  // If it's dark, use a lighter gray for contrast with the almost black background
  if (isDark) {
    return "rgba(60, 60, 60, 0.8)"; // Dark gray with transparency
  }
  
  // For day time, use a semi-transparent darker overlay based on UV level
  if (uvIndex <= 2) {
    return "rgba(70, 130, 180, 0.7)"; // Steel Blue with transparency
  } else if (uvIndex <= 5) {
    return "rgba(139, 69, 19, 0.7)"; // Saddle Brown with transparency  
  } else {
    return "rgba(139, 0, 0, 0.7)"; // Dark Red with transparency
  }
};

/**
 * Get text color for contrast based on UV index and darkness state
 * @param {number} uvIndex - Current UV index (0-11+)
 * @param {boolean} isDark - Whether it's currently dark (after sunset)
 * @returns {string} Hex color code for text
 */
export const getTextColor = (uvIndex, isDark = false) => {
  // If it's dark, use white text for contrast against dark background
  if (isDark) {
    return "#ffffff";
  }
  
  return uvIndex <= 5 ? "#000000" : "#ffffff";
};

/**
 * Format duration in minutes to hours and minutes
 * @param {number} minutes - Duration in minutes
 * @returns {string} Formatted duration string
 */
export const formatDuration = (minutes) => {
  if (minutes < 60) {
    return `${minutes}min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${remainingMinutes}min`;
};